import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const AUTOPLAY_MS = 4000;

export default function HeroCarousel({ slides }) {
  const [index, setIndex] = useState(0);
  const timer = useRef(null);
  const touchX = useRef(null);
  const count = slides.length;

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  useEffect(() => {
    timer.current = setInterval(() => setIndex((i) => (i + 1) % count), AUTOPLAY_MS);
    return () => clearInterval(timer.current);
  }, [count]);

  const pause = () => clearInterval(timer.current);
  const resume = () => {
    clearInterval(timer.current);
    timer.current = setInterval(() => setIndex((i) => (i + 1) % count), AUTOPLAY_MS);
  };

  const onTouchStart = (e) => { pause(); touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 40) prev();
    else if (dx < -40) next();
    touchX.current = null;
    resume();
  };

  return (
    <div
      className="relative w-full select-none"
      data-testid="hero-carousel"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative overflow-hidden border-y border-[#E0B84C]/70 shadow-[0_6px_16px_rgba(0,0,0,0.45)]">
        <div
          className="flex transition-transform duration-[400ms] ease-in-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((s, i) => (
            <Slide key={i} slide={s} active={i === index} />
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        onClick={prev}
        aria-label="Previous slide"
        data-testid="hero-prev"
        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 grid place-items-center text-white/70 hover:text-white active:scale-95 transition-colors drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]"
      >
        <ChevronLeft className="h-7 w-7" strokeWidth={2.5} />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Next slide"
        data-testid="hero-next"
        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 grid place-items-center text-white/70 hover:text-white active:scale-95 transition-colors drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]"
      >
        <ChevronRight className="h-7 w-7" strokeWidth={2.5} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 z-10 flex gap-1.5" data-testid="hero-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === index ? "w-5 bg-gradient-to-r from-[#FFD54A] to-[#F5A800] shadow-[0_0_8px_rgba(245,184,0,0.6)]" : "w-1.5 bg-white/50"}`}
          />
        ))}
      </div>
    </div>
  );
}

function Slide({ slide, active }) {
  const inner = (
    <div className="relative w-full aspect-[3.4/1] overflow-hidden bg-[#0b1017]">
      <img
        src={slide.img}
        alt={slide.title}
        loading={active ? "eager" : "lazy"}
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/45 to-transparent" />
      <div key={active ? "on" : "off"} className={`relative h-full py-1.5 pl-10 pr-10 sm:py-5 sm:pl-16 sm:pr-16 flex flex-col justify-center max-w-[92%] sm:max-w-[85%] ${active ? "bbw-banner-text" : ""}`}>
        {slide.badge && (
          <span className={`inline-flex items-center gap-1 self-start rounded-full text-[8px] sm:text-[11px] font-bold uppercase tracking-wider px-1.5 py-[2px] sm:px-2.5 sm:py-1 leading-none ${slide.badgeClass || "bg-red-600 text-white"}`}>
            {slide.badgeIcon}
            {slide.badge}
          </span>
        )}
        <h2 className="mt-1 sm:mt-2 font-display font-extrabold text-[14px] sm:text-3xl leading-[1.1] line-clamp-1 sm:line-clamp-2 [text-shadow:0_1px_6px_rgba(0,0,0,0.7)]">{slide.title}</h2>
        {slide.subtitle && <p className="text-[10px] sm:text-sm text-slate-200 mt-0.5 sm:mt-1 leading-[1.2] line-clamp-1 sm:line-clamp-none [text-shadow:0_1px_4px_rgba(0,0,0,0.8)]">{slide.subtitle}</p>}
        {slide.highlight && (
          <div className="mt-1 sm:mt-2 self-start rounded bg-black/55 border border-[#E0B84C]/60 px-1.5 py-[2px] sm:px-2.5 sm:py-1.5 max-w-full">
            <p className="text-[9.5px] sm:text-sm font-bold text-[#E0B84C] leading-[1.2] line-clamp-1 sm:line-clamp-none">{slide.highlight}</p>
          </div>
        )}
        {slide.cta && (
          <span className="bbw-btn-gold mt-1 sm:mt-3 inline-flex items-center gap-0.5 self-start rounded-full text-[10px] sm:text-sm font-extrabold px-2 py-[3px] sm:px-4 sm:py-1.5 leading-none">
            {slide.busy ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" /> : null}
            {slide.busy ? "Opening…" : slide.cta} {!slide.busy && <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />}
          </span>
        )}
      </div>
    </div>
  );

  if (slide.to) {
    return (
      <Link to={slide.to} className="block w-full min-w-full shrink-0 group" data-testid={`hero-slide-${slide.key}`}>
        {inner}
      </Link>
    );
  }
  if (slide.onClick) {
    return (
      <button type="button" onClick={slide.onClick} disabled={slide.busy} className="block w-full min-w-full shrink-0 group text-left disabled:opacity-80" data-testid={`hero-slide-${slide.key}`}>
        {inner}
      </button>
    );
  }
  return (
    <div className="block w-full min-w-full shrink-0" data-testid={`hero-slide-${slide.key}`}>
      {inner}
    </div>
  );
}
